/**
 * Stripe Subscription Billing API client — v2
 *
 * Talks to /api/v2/billing/* endpoints.
 * All card collection is done through Stripe.js (Elements)
 * so no sensitive card data ever touches this client or our servers.
 */

import apiClient from './config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubscriptionPlan
{
    id: number;
    name: string;
    description: string | null;
    plan_type: string;
    price_cents: number;
    price_usd: number;
    currency: string;
    billing_interval: string;
    is_active: boolean;
}

export interface BillingAdjustment
{
    id: number;
    source_type: string;
    amount_usd: number;
    currency: string;
    status: string;
    applied_at: string | null;
}

export interface LLMUsageCreditSummary
{
    organization_id: number;
    balance_cents: number;
    balance_usd: number;
    pending_overage_debt_cents: number;
    pending_overage_debt_usd: number;
    free_overage_debt_cents: number;
    free_overage_debt_usd: number;
    available_balance_after_debt_cents: number;
    available_balance_after_debt_usd: number;
    currency: string;
    status: string;
    recharge_amount_cents: number;
    recharge_amount_usd: number;
    auto_recharge_threshold_cents: number;
    auto_recharge_threshold_usd: number;
    last_recharged_at: string | null;
    recharge_required: boolean;
    failure_message: string | null;
    settlement_pending?: boolean;
    settlement_state?: string;
    settlement_amount_cents?: number;
    settlement_amount_usd?: number;
    requires_action?: boolean;
    client_secret?: string | null;
    stripe_payment_intent_id?: string | null;
}

export interface LLMUsageCreditLedgerItem
{
    id: number;
    entry_type: string;
    amount_cents: number;
    amount_usd: number;
    balance_before_cents: number;
    balance_after_cents: number;
    pending_overage_before_cents: number;
    pending_overage_after_cents: number;
    currency: string;
    usage_event_id: number | null;
    payment_id: number | null;
    stripe_payment_intent_id: string | null;
    stripe_charge_id: string | null;
    stripe_receipt_url: string | null;
    stripe_error: string | null;
    created_at: string;
}

export interface LLMUsageCreditLedgerResponse
{
    count: number;
    items: LLMUsageCreditLedgerItem[];
}

export interface SubscriptionStatus
{
    has_active_subscription: boolean;
    subscription_status: string | null;
    stripe_subscription_id: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    cancel_at: string | null;
    plan: SubscriptionPlan | null;
    pending_plan: SubscriptionPlan | null;
    pending_plan_effective_at: string | null;
    platform_metered_tokens: number;
    platform_metered_charge_usd: number;
    byo_recorded_tokens: number;
    byo_recorded_charge_usd: number;
    free_tokens_allocated: number;
    free_tokens_used: number;
    free_tokens_remaining: number;
    free_overage_debt: number;
    free_overage_debt_cents: number;
    free_overage_debt_usd: number;
    llm_usage_credits: LLMUsageCreditSummary | null;
    pending_adjustments: BillingAdjustment[];
    message?: string | null;
    requires_action?: boolean;
    payment_intent_client_secret?: string | null;
}

export interface SetupIntentResponse
{
    client_secret: string;
    customer_id: string;
    publishable_key: string;
}

export interface PublicConfigResponse
{
    publishable_key: string;
}

export interface PaymentMethodRequest
{
    payment_method_id: string;
}

export interface BillingPaymentMethod
{
    has_payment_method: boolean;
    payment_method_id: string | null;
    customer_id: string | null;
    brand: string | null;
    last4: string | null;
    exp_month: number | null;
    exp_year: number | null;
    cardholder_name: string | null;
    funding: string | null;
    country: string | null;
    message?: string | null;
}

export interface SubscribeRequest
{
    payment_method_id?: string | null;
    resubscribe?: boolean;
    plan_id?: number | null;
    byo_key?: boolean;
    idempotency_key?: string;
}

export interface SubscribeResponse
{
    subscription_id: number;
    stripe_subscription_id: string | null;
    status: string;
    current_period_start: string | null;
    current_period_end: string | null;
    message: string;
    requires_action?: boolean;
    client_secret?: string | null;
    payment_intent_status?: string | null;
}

export interface ChangePlanRequest
{
    plan_id: number;
    idempotency_key?: string;
}

export interface ChangePlanResponse
{
    subscription_id: number;
    stripe_subscription_id: string | null;
    status: string;
    action: string;
    current_period_start: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    cancel_at: string | null;
    plan: SubscriptionPlan | null;
    pending_plan: SubscriptionPlan | null;
    pending_plan_effective_at: string | null;
    message: string;
    requires_action?: boolean;
    client_secret?: string | null;
    payment_intent_status?: string | null;
}

export interface CancelResponse
{
    subscription_id: number;
    status: string;
    cancel_at_period_end: boolean;
    cancel_at: string | null;
    message: string;
}

export interface PaymentRecovery
{
    payment_id: number;
    stripe_invoice_id: string;
    stripe_payment_intent_id: string | null;
    status: string;
    requires_action: boolean;
    client_secret: string | null;
    failure_code: string | null;
    failure_message: string | null;
    next_payment_attempt: string | null;
    attempt_count: number;
    is_draft: boolean;
    automatically_finalizes_at: string | null;
}

export interface RecoveryConfirmation
{
    recovered: boolean;
    message: string;
}

export interface RemovePaymentMethodResponse
{
    removed_payment_method_id: string | null;
    customer_id: string | null;
    message: string;
}

export interface QuotaStatus
{
    organization_id: number;
    has_active_subscription: boolean;
    period_start: string | null;
    period_end: string | null;
    subscription_status: string | null;
    cancel_at_period_end: boolean;
    cancel_at: string | null;
    may_run_job: boolean;
    block_reason: string | null;
    // Free tier
    free_tokens_allocated: number;
    free_tokens_used: number;
    free_tokens_remaining: number;
    free_overage_debt: number;
    free_overage_debt_cents: number;
    free_overage_debt_usd: number;
    platform_metered_tokens: number;
    platform_metered_charge_usd: number;
    platform_usage_credit_tokens: number;
    platform_usage_credit_charge_usd: number;
    all_time_paid_platform_tokens: number;
    byo_recorded_tokens: number;
    byo_recorded_charge_usd: number;
    llm_usage_credits: LLMUsageCreditSummary | null;
    // Combined
    active_balance_source: string | null;
    free_quota_source: string | null;
    next_action_required: string | null;
    outstanding_total_overage_debt: number;
}

export interface SubscriptionPayment
{
    id: number;
    amount_cents: number;
    amount_usd: number;
    currency: string;
    status: string;
    refunded_amount_cents?: number;
    stripe_payment_intent_id: string | null;
    stripe_invoice_id: string | null;
    stripe_charge_id?: string | null;
    receipt_url?: string | null;
    payment_type?: string;
    recharge_type?: string | null;
    plan_name_snapshot?: string | null;
    payment_context?: string | null;
    period_start: string | null;
    period_end: string | null;
    paid_at?: string | null;
    created_at: string;
    attempt_id?: number | null;
    row_kind?: 'payment' | 'attempt' | 'refund' | 'related';
    parent_payment_id?: number | null;
    related_payment_created_at?: string | null;
    related_payment_paid_at?: string | null;
    related_payment_amount_cents?: number | null;
    related_payment_receipt_url?: string | null;
    related_payment_period_start?: string | null;
    related_payment_period_end?: string | null;
    refund_events?: Array<{
        id: number;
        amount_cents: number;
        created_at: string;
    }>;
}

export interface PaymentListResponse
{
    count: number;
    items: SubscriptionPayment[];
}

export interface InitialUsageCreditCompletion
{
    state: 'pending' | 'succeeded' | 'skipped' | 'requires_action' | 'failed';
    message?: string | null;
    requires_action: boolean;
    client_secret?: string | null;
    stripe_payment_intent_id?: string | null;
}

export interface BillingPaymentsFilterParams
{
    limit?: number;
    search?: string;
    status?: string;
    payment_type?: string;
    date_from?: string;
    date_to?: string;
}

export interface TokenUsageHistoryItem
{
    id: number;
    job_id: number | null;
    user_id: number | null;
    chat_session_id: string | null;
    usage_group_key: string | null;
    usage_label: string | null;
    model: string | null;
    workflow_profile: string | null;
    stage: string | null;
    provider: string | null;
    key_source: string;
    billable: boolean;
    event_count: number;
    prompt_tokens: number;
    completion_tokens: number;
    cached_input_tokens: number;
    image_tokens: number;
    total_tokens: number;
    billable_tokens: number;
    non_billable_tokens: number;
    customer_charge_usd: number;
    created_at: string;
}

export interface TokenUsageHistoryResponse
{
    count: number;
    items: TokenUsageHistoryItem[];
    ledger_entries: TokenLedgerItem[];
}

export interface BillingUsageFilterParams
{
    limit?: number;
    usage_search?: string;
    usage_workflow_profile?: string;
    usage_key_source?: string;
    usage_billable?: boolean;
    usage_date_from?: string;
    usage_date_to?: string;
    ledger_search?: string;
    ledger_balance_bucket?: string;
    ledger_event_type?: string;
    ledger_date_from?: string;
    ledger_date_to?: string;
}

export interface TokenLedgerItem
{
    id: number;
    event_type: string;
    token_delta: number;
    balance_bucket: string;
    job_id: number | null;
    subscription_id: number | null;
    notes: string | null;
    created_at: string;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

const BASE = '/api/v2/billing';

/** Fetch the single available subscription plan. */
export async function fetchSubscriptionPlan(): Promise<SubscriptionPlan>
{
    const { data } = await apiClient.get<SubscriptionPlan>(`${BASE}/plan`);
    return data;
}

/** Fetch the current organization's subscription status and token quota. */
export async function fetchSubscriptionStatus(): Promise<SubscriptionStatus>
{
    const { data } = await apiClient.get<SubscriptionStatus>(`${BASE}/subscription`);
    return data;
}

/** Fetch frontend-safe Stripe configuration used by the billing page. */
export async function fetchPublicConfig(): Promise<PublicConfigResponse>
{
    const { data } = await apiClient.get<PublicConfigResponse>(`${BASE}/public-config`);
    return data;
}

/**
 * Create a Stripe SetupIntent.
 * Use the returned client_secret with Stripe.js:
 *   stripe.confirmCardSetup(client_secret, { payment_method: { card: cardElement } })
 */
export async function createSetupIntent(): Promise<SetupIntentResponse>
{
    const { data } = await apiClient.post<SetupIntentResponse>(`${BASE}/setup-intent`);
    return data;
}

/** Fetch the currently saved default billing payment method. */
export async function fetchPaymentMethod(): Promise<BillingPaymentMethod>
{
    const { data } = await apiClient.get<BillingPaymentMethod>(`${BASE}/payment-method`);
    return data;
}

/** Save or replace the default billing payment method. */
export async function savePaymentMethod(req: PaymentMethodRequest): Promise<BillingPaymentMethod>
{
    const { data } = await apiClient.post<BillingPaymentMethod>(`${BASE}/payment-method`, req);
    return data;
}

/** Remove the saved default billing payment method. */
export async function removePaymentMethod(): Promise<RemovePaymentMethodResponse>
{
    const { data } = await apiClient.delete<RemovePaymentMethodResponse>(`${BASE}/payment-method`);
    return data;
}

/**
 * Create a subscription using the confirmed PaymentMethod ID from Stripe.js.
 * Call after confirmCardSetup() returns a setupIntent.payment_method.
 */
export async function createSubscription(req: SubscribeRequest = {}): Promise<SubscribeResponse>
{
    const { data } = await apiClient.post<SubscribeResponse>(`${BASE}/subscribe`, req);
    return data;
}

/** Change the active paid subscription plan. Upgrades apply immediately; downgrades are scheduled. */
export async function changeSubscriptionPlan(req: ChangePlanRequest): Promise<ChangePlanResponse>
{
    const { data } = await apiClient.post<ChangePlanResponse>(`${BASE}/change-plan`, req);
    return data;
}

/**
 * Sync subscription status from Stripe on Billing-page entry, explicit refresh,
 * and after the frontend completes a 3DS challenge.
 */
export async function syncSubscription(): Promise<SubscribeResponse>
{
    const { data } = await apiClient.post<SubscribeResponse>(`${BASE}/sync-subscription`);
    return data;
}

/** Cancel the active subscription at period end. */
export async function cancelSubscription(): Promise<CancelResponse>
{
    const { data } = await apiClient.post<CancelResponse>(`${BASE}/cancel`);
    return data;
}

/** Fetch current token quota status for the organization. */
export async function fetchQuotaStatus(): Promise<QuotaStatus>
{
    const { data } = await apiClient.get<QuotaStatus>(`${BASE}/quota`);
    return data;
}

/** Fetch prepaid LLM Usage Credits balance and recharge state. */
export async function fetchLLMUsageCredits(): Promise<LLMUsageCreditSummary>
{
    const { data } = await apiClient.get<LLMUsageCreditSummary>(`${BASE}/llm-usage-credits`);
    return data;
}

/** Fetch recent LLM Usage Credits ledger entries. */
export async function fetchLLMUsageCreditLedger(limit = 50): Promise<LLMUsageCreditLedgerResponse>
{
    const { data } = await apiClient.get<LLMUsageCreditLedgerResponse>(`${BASE}/llm-usage-credits/ledger`, {
        params: { limit },
    });
    return data;
}

/** Manually recharge LLM Usage Credits using the saved payment method. */
export async function rechargeLLMUsageCredits(idempotencyKey?: string): Promise<LLMUsageCreditSummary>
{
    const { data } = await apiClient.post<LLMUsageCreditSummary>(
        `${BASE}/llm-usage-credits/recharge`,
        { idempotency_key: idempotencyKey },
    );
    return data;
}

export async function confirmLLMUsageCreditsRecharge(paymentIntentId: string): Promise<LLMUsageCreditSummary>
{
    const { data } = await apiClient.post<LLMUsageCreditSummary>(
        `${BASE}/llm-usage-credits/recharge/confirm`,
        { payment_intent_id: paymentIntentId },
    );
    return data;
}

/** Fetch the newest unpaid subscription invoice that can be recovered. */
export async function fetchPaymentRecovery(): Promise<PaymentRecovery | null>
{
    const { data } = await apiClient.get<PaymentRecovery | null>(`${BASE}/payment-recovery`);
    return data;
}

/** Retry an unpaid subscription invoice with the organization's saved payment method. */
export async function retrySubscriptionPayment(stripeInvoiceId?: string): Promise<PaymentRecovery | null>
{
    const { data } = await apiClient.post<PaymentRecovery | null>(
        `${BASE}/payment-recovery/retry`,
        { stripe_invoice_id: stripeInvoiceId },
    );
    return data;
}

/** Confirm that Stripe completed authentication and finalized the subscription invoice. */
export async function confirmSubscriptionPayment(paymentIntentId: string): Promise<RecoveryConfirmation>
{
    const { data } = await apiClient.post<RecoveryConfirmation>(
        `${BASE}/payment-recovery/confirm`,
        { payment_intent_id: paymentIntentId },
    );
    return data;
}

/** Fetch billing payment history for the organization. */
export async function fetchPaymentHistory(filters?: BillingPaymentsFilterParams): Promise<PaymentListResponse>
{
    const { data } = await apiClient.get<PaymentListResponse>(`${BASE}/payments`, {
        params: filters,
    });
    return data;
}

/** Fetch the initial LLM Usage Credits payment created with a new subscription. */
export async function fetchInitialUsageCreditPreflight(): Promise<InitialUsageCreditCompletion>
{
    const { data } = await apiClient.get<InitialUsageCreditCompletion>(
        `${BASE}/llm-usage-credits/initial-recharge-preflight`,
    );
    return data;
}

/** Fetch the initial LLM Usage Credits payment created with a new subscription. */
export async function fetchInitialUsageCreditCompletion(subscriptionId: number): Promise<InitialUsageCreditCompletion>
{
    const { data } = await apiClient.get<InitialUsageCreditCompletion>(
        `${BASE}/llm-usage-credits/initial-recharge-status`,
        { params: { subscription_id: subscriptionId } },
    );
    return data;
}

/** Fetch recent token usage entries for the organization. */
export async function fetchTokenUsageHistory(filters?: BillingUsageFilterParams): Promise<TokenUsageHistoryResponse>
{
    const { data } = await apiClient.get<TokenUsageHistoryResponse>(`${BASE}/token-usage-history`, {
        params: {
            limit: filters?.limit ?? 20,
            ...filters,
        },
    });
    return data;
}
